import React, { useEffect, useState } from "react";
import TablePagination from "@mui/material/TablePagination";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { GetAllJournals } from "../../utils/APIRequest";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Fade,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Tab,
  Stack,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ThumbUp as ThumbUpIcon,
  Build as BuildIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Edit,
  Close as CloseIcon,
  Check,
  AddComment as AddCommentIcon,
  ThumbDown,
  FileDownload,
} from "@mui/icons-material";
import BookIcon from "@mui/icons-material/Book";
import { keyframes } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/formatDate";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Smooth animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const AdminJournals: React.FC = () => {
  const [journals, setJournals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJournals = async () => {
      setLoading(true);
      try {
        const data = await GetAllJournals();
        const journalsArr = Array.isArray(data) ? data : [];
        setJournals(journalsArr);
      } catch (error) {
        setJournals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJournals();
  }, []);

  // Filter journals by selected date
  const filteredJournals = selectedDate
    ? journals.filter((j) => {
      if (!j.dateOfJournal) return false;
      // Compare only the date part
      return dayjs(j.dateOfJournal).isSame(selectedDate, "day");
    })
    : journals;

  // Pagination logic
  const paginatedJournals = filteredJournals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const title = "Employee Journals";

    const headers = [
      [
        "Sl.No",
        "Date Of Journal",
        "Employee",
        "Team Lead",
        "Type",
        "Category",
        "Employee Feedback",
        "TL Feedback",
        "Action",
      ],
    ];

    const rows = filteredJournals.map((j, idx) => [
      idx + 1,
      formatDate(j.dateOfJournal),
      j.user?.emp_name || "",
      j.teamLead || "",
      j.feedbackType || "",
      j.feedbackCategory || "",
      (j.employeeFeedback || "").replace(/[\r\n]+/g, " "),
      (j.tlFeedback || "-").replace(/[\r\n]+/g, " "),
      j.actions === true
        ? "Approved"
        : j.actions === false
          ? "Rejected"
          : "Pending",
    ]);

    doc.setFontSize(14);
    doc.text(title, 14, 20);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 30,
      styles: {
        fontSize: 7,
        cellWidth: "wrap",
      },
      columnStyles: {
        6: { cellWidth: 60 },
        7: { cellWidth: 50 },
      },
      headStyles: { fillColor: [63, 81, 181] },
      margin: { top: 30 },
    });

    doc.save("journals_export.pdf");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#F8FAF6",
        py: 4,
      }}
    >
      {/* Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: `linear-gradient(135deg,${theme.palette.primary.main},${theme.palette.primary.dark})`,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate("/")}
          >
            <ArrowBackIcon />
          </IconButton>
          <BookIcon
            sx={{ mr: 2, color: theme.palette.secondary.contrastText }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: theme.palette.secondary.contrastText,
            }}
          >
            Employee Journal
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl">
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={setSelectedDate}
                format="DD/MM/YYYY"
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { maxWidth: 200, background: "#fff" },
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />
            </LocalizationProvider>

            <Chip
              label={`Total Journals: ${filteredJournals.length}`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={exportToPDF}
              disabled={!filteredJournals.length}
            >
              Export Journals
            </Button>
          </Stack>
        </Box>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
            }}
          >
            <CircularProgress size={48} />
          </Box>
        ) : (
          <Fade in timeout={800}>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                animation: `${fadeInUp} 0.6s ease-out 0.4s both`,
                background: theme.palette.background.paper,
                width: "92vw",
                maxWidth: "92vw",
                margin: "0 auto",
              }}
            >
              <Table
                sx={{
                  minWidth: 1200,
                  width: "92vw",
                  background: theme.palette.background.paper,
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
                    }}
                  >
                    <TableCell
                      sx={{
                        width: "5%",
                        color: theme.palette.primary.contrastText,
                        fontWeight: 700,
                        fontSize: "1rem",
                        border: 0,
                      }}
                    >
                      Sl.No
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "10%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Date Of Journal
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "10%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Employee
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "10%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Team Lead
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "8%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "12%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "20%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Employee Feedback
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "15%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      TL Feedback
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "10%",
                        color: "#fff",
                        fontWeight: 700,
                        border: 0,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedJournals.map((j, idx) => (
                    <TableRow key={j._id || idx}>
                      <TableCell>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.contrastText,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "1rem",
                            margin: "0 auto",
                          }}
                        >
                          {page * rowsPerPage + idx + 1}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="left"
                          spacing={1}
                          justifyContent="left"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {formatDate(j.dateOfJournal)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" alignItems="left" spacing={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {j.user?.emp_name || ""}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" alignItems="left" spacing={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {j.teamLead || ""}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="left"
                          spacing={1}
                          justifyContent="left"
                        >
                          {j.feedbackType === "Positive" ? (
                            <ThumbUpIcon
                              sx={{
                                color: theme.palette.success.main,
                                fontSize: 20,
                              }}
                            />
                          ) : (
                            <ThumbDown
                              sx={{
                                color: theme.palette.warning.main,
                                fontSize: 20,
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                j.feedbackType === "Positive"
                                  ? theme.palette.success.main
                                  : theme.palette.warning.main,
                              fontWeight: 600,
                              fontSize: "0.85rem",
                            }}
                          >
                            {j.feedbackType || ""}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={j.feedbackCategory}
                          size="medium"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.main,
                            backgroundColor:
                              theme.palette.secondary.light + "22",
                            fontSize: "0.85rem",
                            height: "30px",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", padding: "16px" }}>
                        {j.employeeFeedback &&
                          j.employeeFeedback.length > 200 ? (
                          <Tooltip
                            title={j.employeeFeedback}
                            placement="bottom"
                            enterDelay={300}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                                lineHeight: 1.4,
                                color: theme.palette.text.primary,
                                fontSize: "0.9rem",
                                cursor: "pointer",
                              }}
                            >
                              {j.employeeFeedback.slice(0, 200)}...
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 6,
                              WebkitBoxOrient: "vertical",
                              lineHeight: 1.4,
                              color: theme.palette.text.primary,
                              fontSize: "0.9rem",
                              cursor: "pointer",
                            }}
                          >
                            {j.employeeFeedback}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", padding: "16px" }}>
                        {j.tlFeedback && j.tlFeedback.length > 100 ? (
                          <Tooltip
                            title={j.tlFeedback}
                            placement="bottom"
                            enterDelay={300}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                                lineHeight: 1.4,
                                color: theme.palette.text.primary,
                                fontSize: "0.9rem",
                                cursor: "pointer",
                              }}
                            >
                              {j.tlFeedback.slice(0, 100)}...
                            </Typography>
                          </Tooltip>
                        ) : j.tlFeedback ? (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: "vertical",
                              lineHeight: 1.4,
                              color: theme.palette.text.primary,
                              fontSize: "0.9rem",
                              cursor: "pointer",
                            }}
                          >
                            {j.tlFeedback}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontStyle: "italic",
                              textAlign: "left",
                              fontSize: "0.9rem",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="left"
                          alignItems="center"
                        >
                          {j.actions === true ? (
                            <Chip
                              label="Approved"
                              color="success"
                              size="small"
                            />
                          ) : j.actions === false ? (
                            <Chip label="Rejected" color="error" size="small" />
                          ) : (
                            <Chip
                              label="Pending"
                              color="default"
                              size="small"
                            />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Fade>
        )}
        {filteredJournals.length === 0 && (
          <div style={{ marginTop: 16 }}>No records found.</div>
        )}

        {/* Table Pagination */}
        <TablePagination
          component="div"
          count={filteredJournals.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
          sx={{ mt: 2, borderTop: "1px solid #eee" }}
        />
      </Container>
    </Box>
  );
};

export default AdminJournals;
